import { useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAlertService } from '@/shared/lib/useAlertService'
import { ParentSearchFormData, ParentSearchResult } from '../coparent-page/type'
import { ParentsService } from '@/shared/api/rest/services/parents.service'
import { BranchesService } from '@/shared/api/rest/services/branches.service'

const parentSearchSchema = yup.object({
    searchTerm: yup
        .string()
        .required('Search term is required')
        .min(2, 'Search term must be at least 2 characters')
})

export default function useParentSearch() {
    const alertService = useAlertService()
    const [parents, setParents] = useState<ParentSearchResult[]>([])
    const [loading, setLoading] = useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<ParentSearchFormData>({
        resolver: yupResolver(parentSearchSchema)
    })

    const searchHandler = async (data: ParentSearchFormData) => {
        setLoading(true)
        try {
            const [results, branches] = await Promise.all([
                ParentsService.byName(data.searchTerm),
                BranchesService.list().catch(() => [])
            ])
            const mapped: ParentSearchResult[] = (results ?? []).map((p) => ({
                _id: p._id,
                firstName: p.firstName ?? '',
                lastName: p.lastName ?? '',
                picture: (p as { picture?: string }).picture,
                branch: p.branchId
                    ? {
                          _id: p.branchId,
                          name: branches.find((b) => b._id === p.branchId)?.name ?? ''
                      }
                    : undefined
            }))
            setParents(mapped)
            if (mapped.length === 0) {
                alertService.info('No parents found matching your search')
            }
        } catch (error) {
            alertService.error(
                error instanceof Error ? error.message : 'Search Error',
                'Search Error'
            )
            setParents([])
        } finally {
            setLoading(false)
        }
    }

    return {
        parents,
        loading,
        register,
        handleSubmit: handleSubmit(searchHandler),
        errors
    }
}
